import _extends from "@babel/runtime/helpers/extends";
import { color } from "@smartsheet/design-tokens-react";
import { useActive } from "@smartsheet/lodestar-utils";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { uid } from "react-uid";
import { useTheme } from "../../theme/utils/useTheme";
import { Handle } from "./styled/Handle";
import { Input } from "./styled/Input";
import { Label } from "./styled/Label";
import { LabelText } from "./styled/LabelText";
import { Slide } from "./styled/Slide";
import { ToggleOffIcon } from "./styled/ToggleOffIcon";
import { ToggleOnIcon } from "./styled/ToggleOnIcon";
const iconSizes = {
  large: "medium",
  medium: "xSmall"
};

/**
 * This toggle does all your toggling.
 */
export const Toggle = _ref => {
  let {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    isChecked,
    onClick,
    className,
    clientId,
    id,
    isDisabled = false,
    isInvalid = false,
    label = "",
    labelPlacement = "end",
    name,
    onBlur = () => {},
    onFocus = () => {},
    tabIndex = 0,
    value,
    size = "large"
  } = _ref;
  const {
    key
  } = useTheme();
  const themeMode = key;
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const setHoveredTrue = useCallback(() => setIsHovered(true), []);
  const setHoveredFalse = useCallback(() => setIsHovered(false), []);
  const handleBlur = event => {
    setIsFocused(false);
    onBlur(event);
  };
  const handleFocus = event => {
    setIsFocused(true);
    onFocus(event);
  };
  const handleClick = event => {
    if (isDisabled) {
      return;
    }
    // The default action of a label around an input is to re-trigger the click event on the
    // input element. We need to ignore the first event on the label here or we'll call this
    // handler twice since the event will be re-fired on the input as well.
    // We could preventDefault in the first event, but for screen-reader support we must
    // have the event on the input element be handled, or the SR won't say "Space, on".
    // It would just say "Space" and not provide the new state, or say "off" all the time??!
    if (label && event.target !== inputRef.current) {
      event.stopPropagation();
      return;
    }
    onClick(event);
  };
  const {
    isActive,
    ...isActiveHandlers
  } = useActive({
    onMouseEnter: setHoveredTrue,
    onMouseLeave: setHoveredFalse
  });
  const defaultId = useMemo(() => uid({
    id: "Toggle"
  }), []);
  const idToUse = id || defaultId;
  const labelId = `${idToUse}-label`;

  // Allow multiple labels to be applied.
  // If supplied, the prop aria-labelledby will be applied first, as it will
  // be read first, followed by the more closely scoped local label.
  const finalLabelledBy = [ariaLabelledby, label && labelId || false].filter(Boolean).join(" ") || undefined;
  const labelText = label ? /*#__PURE__*/React.createElement(LabelText, {
    size: size,
    id: labelId,
    isDisabled: isDisabled,
    placement: labelPlacement,
    themeMode: themeMode
  }, label) : undefined;

  // If there's no text label prop provided, this component will fail
  // some a11y scanners due to there being an empty <label>. This happens
  // even when an aria-label is provided anyways since that is stuck on
  // the inner input and the label is still empty.
  // If this is the case, we'll just use a div here.
  const Root = useMemo(() => label ? Label : Label.withComponent("div"), [label]);
  const getToggleIcons = () => {
    switch (themeMode) {
      case "ursa-light":
      case "ursa-dark":
        return /*#__PURE__*/React.createElement(React.Fragment, null);
      case "lodestar-light":
      case "oldestar":
      default:
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ToggleOnIcon, {
          visible: isChecked,
          themeMode: themeMode,
          color: color("color--icons--neutral-inverse--default"),
          size: iconSizes[size],
          toggleSize: size
        }), /*#__PURE__*/React.createElement(ToggleOffIcon, {
          visible: !isChecked,
          themeMode: themeMode,
          color: color("color--icons--neutral-inverse--default"),
          size: iconSizes[size],
          toggleSize: size
        }));
    }
  };
  return /*#__PURE__*/React.createElement(Root, {
    "data-client-id": clientId,
    className: className,
    onClick: handleClick
  }, label && labelPlacement === "start" && labelText, /*#__PURE__*/React.createElement(Slide, _extends({
    isActive: isActive,
    isChecked: isChecked,
    isDisabled: isDisabled,
    isFocused: isFocused,
    isInvalid: isInvalid,
    themeMode: themeMode,
    size: size
  }, isActiveHandlers), /*#__PURE__*/React.createElement(Input, _extends({
    "aria-label": ariaLabel,
    "aria-labelledby": finalLabelledBy,
    "aria-describedby": ariaDescribedby,
    "aria-disabled": isDisabled,
    "aria-readonly": isDisabled,
    id: idToUse,
    name: name,
    onBlur: handleBlur,
    onChange: noopChangeHandler,
    onFocus: handleFocus,
    role: "switch",
    type: "checkbox",
    "aria-checked": isChecked
  }, value !== undefined && {
    value
  }, {
    "data-client-id": clientId && `${clientId}--input`,
    ref: inputRef,
    tabIndex: isDisabled ? -1 : tabIndex
  })), getToggleIcons(), /*#__PURE__*/React.createElement(Handle, {
    isChecked: isChecked,
    isDisabled: isDisabled,
    isHovered: isHovered,
    isInvalid: isInvalid,
    size: size,
    themeMode: themeMode
  })), label && labelPlacement === "end" && labelText);
};
const noopChangeHandler = () => {
  // This is provided to the onChange handler of the input. It's only
  // here because React throws out a warning if you don't include it AND the value prop.
  // However, we do something special with the onClick event in the label
  // so we're all good.
  // Quiet please, React. ty.
};
//# sourceMappingURL=Toggle.js.map